import React, { SetStateAction, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { parse, format } from "date-fns";
import InputDatePicker from "../common/InputDatePicker";
import { TimePicker } from "antd";
import dayjs from "dayjs";
// import { formatForInput, formatForState } from "../utils/date-format-method";

interface ModalProps {
  // preferences: any;
  // setPreferences: React.Dispatch<SetStateAction<any>>;
  isSubmitting: boolean;
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
  handleSubmit: (d: any) => void;
  selectedProperty?: any;
}

interface PrefProps {
  room_options: string | null;
  gender: string | null;
  need_meal: string | null;
  visit_date: string | null;
  visit_time: string | null;
}

type ErrorType = Record<keyof PrefProps, string> | null;

const roomTypes = [
  "Single Sharing",
  "Double Sharing",
  "Triple Sharing",
  "Multi Sharing",
];

const genderTypes = [
  { value: "Male", label: "Boys" },
  { value: "Female", label: "Girls" },
  { value: "Others", label: "Co-Living" },
];

const mealTypes = [
  { value: "Yes", label: "Meal" },
  { value: "No", label: "No Meal" },
];

const btnStyle = {
  padding: "8px 16px",
  border: "1px solid #ccc",
  borderRadius: "6px",
  backgroundColor: "#fff",
  cursor: "pointer",
  fontSize: "14px",
};

const errorStyle = {
  color: "#dc3545",
  fontSize: 12.5,
  marginTop: 8,
  marginBottom: 7,
};

const ContactOwnerModal: React.FC<ModalProps> = ({
  setIsOpen,
  handleSubmit,
  selectedProperty,
  isSubmitting = false,
}) => {
  const [preferences, setPreferences] = useState<PrefProps>({
    room_options: null,
    gender: null,
    need_meal: null,
    visit_date: null,
    visit_time: null,
  });
  const [errors, setErrors] = useState<ErrorType>(null);

  // --- clear error ---
  const handleClearError = (field: string) => {
    if (!errors) return;
    setErrors((prev: any) => {
      if (prev[field]) {
        const { [field]: _, ...rest } = prev;
        return rest;
      } else {
        return prev;
      }
    });
  };

  // --- handle change ---
  const handleChange = (field: string, value: string) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
    handleClearError(field);
  };

  // --- validate ---
  const validate = (data: PrefProps) => {
    const newError: Record<string, string> = {};

    if (!data.room_options) {
      newError.room_options = "Room type is required.";
    }
    if (!data.gender) {
      newError.gender = "Gender is required.";
    }
    if (!data.need_meal) {
      newError.need_meal = "Meal preference is required.";
    }
    if (!data.visit_date) {
      newError.visit_date = "Visit date is required.";
    }
    if (!data.visit_time) {
      newError.visit_time = "Visit time is required.";
    }

    setErrors(newError);
    return Object.keys(newError).length === 0;
  };

  // --- handle submit ---
  const handleModalSubmit = async (e: any) => {
    e.preventDefault();
    const isValid = validate(preferences);
    if (!isValid) return null;
    // console.log("isvalid:", isValid);
    localStorage.setItem("preferrences", JSON.stringify(preferences));
    handleSubmit(preferences);
  };

  useEffect(() => {
    const storedPrefs = localStorage.getItem("preferrences");
    if (!!storedPrefs) {
      setPreferences(JSON.parse(storedPrefs));
    }
  }, []);

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        id="contact-owner-modal"
        style={{ minWidth: 400, maxWidth: 500 }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "12px",
          }}
        >
          <h2 style={{ margin: 0 }}>Preferences</h2>
          <button style={{}} onClick={() => setIsOpen(false)}>
            ✕
          </button>
        </div>

        <hr style={{ border: "none", borderTop: "1px solid #E0E0E0" }} />

        <div
          style={{
            marginTop: 18,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            fontSize: 15,
          }}
        >
          {/* Room Type */}
          <div>
            <h4 style={{ fontWeight: "600", color: "#2d2d2d" }}>Room Type</h4>
            {errors?.room_options && (
              <div style={errorStyle}>{errors.room_options}</div>
            )}
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                rowGap: 9,
              }}
            >
              {roomTypes.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleChange("room_options", tab)}
                  style={{
                    padding: "7px 12px",
                    fontSize: "12.5px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    backgroundColor:
                      preferences.room_options === tab
                        ? "var(--primary-color)"
                        : "#f4f4f4",
                    color: preferences.room_options === tab ? "#fff" : "#333",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Gender Type */}
          <div style={{ marginTop: 10 }}>
            <h4 style={{ fontWeight: "600", color: "#2d2d2d" }}>Gender Type</h4>
            {errors?.gender && <div style={errorStyle}>{errors.gender}</div>}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 14,
                marginTop: 10,
              }}
            >
              {genderTypes.map(({ value, label }) => (
                <label
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={label}
                    checked={preferences.gender === value}
                    onChange={() => handleChange("gender", value)}
                    style={{
                      accentColor: "var(--primary-color)",
                      transform: "scale(0.8)",
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      margin: 0,
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Meal Type */}
          <div>
            <h4 style={{ fontWeight: "600", color: "#2d2d2d" }}>Meal Type</h4>
            {errors?.need_meal && (
              <div style={errorStyle}>{errors.need_meal}</div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 14,
                marginTop: 10,
              }}
            >
              {mealTypes.map(({ value, label }) => (
                <label
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  <input
                    type="radio"
                    name="need_meal"
                    value={label}
                    checked={preferences.need_meal === value}
                    // defaultChecked={preferences?.need_meal === value}
                    onChange={() => handleChange("need_meal", value)}
                    style={{
                      accentColor: "var(--primary-color)",
                      transform: "scale(0.8)",
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      margin: 0,
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Planned Visit Date  */}
          <div
            style={{
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                // background: "gray",
              }}
            >
              <h4 style={{ fontWeight: "600", color: "#2d2d2d" }}>
                Planned Visit Date:
              </h4>

              {/* <div style={{ marginTop: 10 }}> */}
              <InputDatePicker
                value={preferences?.visit_date}
                onChange={(val) => {
                  setPreferences((prev) => ({ ...prev, visit_date: val }));
                  handleClearError("visit_date");
                }}
                maxWidth={180}
              />
            </div>
            {errors?.visit_date && (
              <div style={errorStyle}>{errors.visit_date}</div>
            )}
          </div>

          {/* Planned Visit Time  */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h4 style={{ fontWeight: "600", color: "#2d2d2d" }}>
                Planned Visit Time:
              </h4>

              <div style={{ width: "100%", maxWidth: "180px" }}>
                <TimePicker
                  format="HH:mm"
                  value={
                    preferences?.visit_time
                      ? dayjs(preferences?.visit_time, "HH:mm")
                      : null
                  }
                  onSelect={(time) => {
                    setPreferences((prev) => ({
                      ...prev,
                      visit_time: time ? time.format("HH:mm") : null,
                    }));
                    handleClearError("visit_time");
                  }}
                  popupClassName="my-timepicker-popup"
                  style={{ width: "100%", height: "35px" }}
                  inputReadOnly
                />
              </div>
            </div>
            {errors?.visit_time && (
              <div style={errorStyle}>{errors.visit_time}</div>
            )}
          </div>
        </div>
        {/* <>{console.log(preferences)}</> */}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button onClick={() => setIsOpen(false)} style={btnStyle}>
            Cancel
          </button>

          <button
            style={{
              ...btnStyle,
              color: "white",
              backgroundColor: "var(--primary-color)",
              opacity: isSubmitting ? 0.3 : 1,
            }}
            onClick={handleModalSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactOwnerModal;
